/**
 * Geographic Investment Map — Country Data Table
 *
 * MVP implementation: country-based data table (interactive map in Enterprise+ tier).
 * Shows investment distribution by country with expandable investee details.
 *
 * Data from GET /geo/investment-map.
 * Summary stats at top: total countries, total investments, total EUR.
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Globe,
  ChevronDown,
  ChevronRight,
  Map,
  TrendingUp,
  Building2,
} from 'lucide-react';
import { apiClient } from '@/api/client';

// ─── Types ──────────────────────────────────────────────────────

interface GeoInvestee {
  investee_id: number;
  investee_name: string;
  investment_count: number;
  total_eur: number;
  instrument_type: string;
}

interface GeoCountry {
  country_code: string;
  country_name: string;
  investment_count: number;
  total_eur: number;
  investee_count: number;
  primary_instrument: string;
  investees: GeoInvestee[];
}

interface GeoMapData {
  countries: GeoCountry[];
  summary: {
    total_countries: number;
    total_investments: number;
    total_eur: number;
  };
}

// ─── Data Fetching ──────────────────────────────────────────────

function useInvestmentMap() {
  return useQuery<GeoMapData>({
    queryKey: ['ic', 'geo', 'investment-map'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/geo/investment-map');
      return res.data?.data ?? res.data;
    },
    staleTime: 120_000,
  });
}

// ─── Country Flag Emoji ─────────────────────────────────────────

function countryFlag(code: string): string {
  const upper = code.toUpperCase();
  if (upper.length !== 2) return '';
  const first = upper.codePointAt(0);
  const second = upper.codePointAt(1);
  if (first === undefined || second === undefined) return '';
  return String.fromCodePoint(first + 0x1F1A5) + String.fromCodePoint(second + 0x1F1A5);
}

// ─── Main Component ─────────────────────────────────────────────

export function GeographicPage() {
  const mapQuery = useInvestmentMap();
  const data = mapQuery.data;

  const sortedCountries = useMemo(() => {
    if (!data?.countries) return [];
    return [...data.countries].sort((a, b) => b.total_eur - a.total_eur);
  }, [data]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-ic-text-primary">Geographic Distribution</h1>
        <p className="mt-0.5 text-xs text-ic-text-tertiary">
          Investment distribution by country across your portfolio
        </p>
      </div>

      {/* Summary Stats */}
      {mapQuery.isLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-slate-700/50" />
          ))}
        </div>
      ) : data ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SummaryCard
            label="Countries"
            value={(data.summary?.total_countries ?? 0).toString()}
            icon={<Globe className="h-4 w-4" />}
          />
          <SummaryCard
            label="Total Investments"
            value={(data.summary?.total_investments ?? 0).toString()}
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <SummaryCard
            label="Total EUR"
            value={formatEUR(data.summary?.total_eur ?? 0)}
            icon={<Building2 className="h-4 w-4" />}
          />
        </div>
      ) : null}

      {/* Map Placeholder */}
      <div className="flex items-center gap-2 rounded-md border border-dashed border-ic-border bg-ic-card/50 px-4 py-3">
        <Map className="h-4 w-4 text-ic-text-tertiary" />
        <span className="text-xs text-ic-text-tertiary">
          Interactive map available in Enterprise+ tier
        </span>
      </div>

      {/* Country Table */}
      <div className="rounded-lg border border-ic-border bg-ic-card">
        <div className="border-b border-ic-border p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
            Investments by Country
          </h2>
        </div>

        {mapQuery.isLoading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-slate-700/50" />
            ))}
          </div>
        ) : sortedCountries.length === 0 ? (
          <div className="flex h-40 items-center justify-center">
            <p className="text-xs text-ic-text-tertiary">No investment data available</p>
          </div>
        ) : (
          <CountryTable countries={sortedCountries} />
        )}
      </div>
    </div>
  );
}

// ─── Summary Card ───────────────────────────────────────────────

interface SummaryCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
}

function SummaryCard({ label, value, icon }: SummaryCardProps) {
  return (
    <div className="rounded-lg border border-ic-border bg-ic-card p-4">
      <div className="flex items-center gap-2 text-ic-text-tertiary">
        {icon}
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 text-xl font-semibold text-ic-text-primary tracking-tight">{value}</p>
    </div>
  );
}

// ─── Country Table ──────────────────────────────────────────────

function CountryTable({ countries }: { countries: GeoCountry[] }) {
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null);

  const toggleExpand = useCallback((code: string) => {
    setExpandedCountry((prev) => (prev === code ? null : code));
  }, []);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs ic-table">
        <thead>
          <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
            <th className="py-3 pl-4 pr-2 font-medium w-8" />
            <th className="py-3 px-3 font-medium">Country</th>
            <th className="py-3 px-3 font-medium text-right">Investments</th>
            <th className="py-3 px-3 font-medium text-right">Total EUR</th>
            <th className="py-3 px-3 font-medium text-right">Investees</th>
            <th className="py-3 px-3 pr-4 font-medium">Primary Instrument</th>
          </tr>
        </thead>
        <tbody>
          {countries.map((country) => {
            const isExpanded = expandedCountry === country.country_code;
            return (
              <CountryRow
                key={country.country_code}
                country={country}
                isExpanded={isExpanded}
                onToggle={() => toggleExpand(country.country_code)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Country Row ────────────────────────────────────────────────

interface CountryRowProps {
  country: GeoCountry;
  isExpanded: boolean;
  onToggle: () => void;
}

function CountryRow({ country, isExpanded, onToggle }: CountryRowProps) {
  const flag = countryFlag(country.country_code);
  const investeesList = Array.isArray(country.investees) ? country.investees : [];
  const hasInvestees = investeesList.length > 0;

  return (
    <>
      <tr
        className="border-b border-ic-border/50 cursor-pointer hover:bg-ic-hover/50 transition-colors"
        onClick={onToggle}
      >
        <td className="py-3 pl-4 pr-2">
          {hasInvestees ? (
            isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-ic-text-tertiary" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-ic-text-tertiary" />
            )
          ) : (
            <span className="inline-block w-3.5" />
          )}
        </td>
        <td className="py-3 px-3">
          <div className="flex items-center gap-2">
            <span className="text-base leading-none">{flag}</span>
            <span className="text-xs font-medium text-ic-text-primary">
              {country.country_name}
            </span>
            <span className="text-[10px] text-ic-text-tertiary">
              {country.country_code}
            </span>
          </div>
        </td>
        <td className="py-3 px-3 text-right font-mono text-xs text-ic-text-secondary">
          {country.investment_count}
        </td>
        <td className="py-3 px-3 text-right font-mono text-xs text-ic-text-secondary">
          {formatEUR(country.total_eur)}
        </td>
        <td className="py-3 px-3 text-right font-mono text-xs text-ic-text-secondary">
          {country.investee_count}
        </td>
        <td className="py-3 px-3 pr-4 text-xs text-ic-text-tertiary">
          {country.primary_instrument || '--'}
        </td>
      </tr>

      {/* Expanded Investee Details */}
      {isExpanded && hasInvestees && (
        <tr>
          <td colSpan={6} className="bg-ic-surface/50 px-4 py-2">
            <InvesteeDetails investees={investeesList} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Investee Details (Expanded) ────────────────────────────────

function InvesteeDetails({ investees }: { investees: GeoInvestee[] }) {
  return (
    <div className="ml-8 space-y-0">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[9px] uppercase tracking-wider text-ic-text-tertiary">
            <th className="pb-1.5 pr-3 font-medium">Investee</th>
            <th className="pb-1.5 px-3 font-medium text-right">Investments</th>
            <th className="pb-1.5 px-3 font-medium text-right">Total EUR</th>
            <th className="pb-1.5 px-3 font-medium">Instrument</th>
          </tr>
        </thead>
        <tbody>
          {investees.map((inv) => (
            <tr key={inv.investee_id} className="border-t border-ic-border/30">
              <td className="py-1.5 pr-3 text-ic-text-secondary">{inv.investee_name}</td>
              <td className="py-1.5 px-3 text-right font-mono text-ic-text-tertiary">
                {inv.investment_count}
              </td>
              <td className="py-1.5 px-3 text-right font-mono text-ic-text-tertiary">
                {formatEUR(inv.total_eur)}
              </td>
              <td className="py-1.5 px-3 text-ic-text-tertiary">
                {inv.instrument_type || '--'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Formatters ─────────────────────────────────────────────────

function formatEUR(amount: number | null | undefined): string {
  const val = amount ?? 0;
  if (val >= 1_000_000_000) return `EUR ${(val / 1_000_000_000).toFixed(1)}B`;
  if (val >= 1_000_000) return `EUR ${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `EUR ${(val / 1_000).toFixed(0)}K`;
  if (val === 0) return 'EUR 0';
  return `EUR ${val.toLocaleString()}`;
}
